/**
 * The address as state: one reader and one writer for every page that keeps
 * its view in the query string (KPR-1579). `hooks/useUrlParams` is the hook
 * over them; `hooks/useUrlListParams` (the admin lists), `hooks/useListQuery`
 * (`?q=`), `hooks/useItemsViewParams` (the items page) and `hooks/useTabParam`
 * are built on it, and `utils/itemsView` `readItemsView` reads the items
 * page's contract with it for the surfaces outside the page.
 *
 * A spec maps a key to how its param reads:
 *
 *   {
 *     q:      { default: '' },                                   // trimmed text
 *     status: { values: ['sent', 'failed'] },                    // an allowlist
 *     derived:{ default: false, kind: 'boolean' },               // 'true' reads true
 *     runsPage:{ default: 1, kind: 'page' },                     // a whole number ≥ 1
 *     group:  { default: 'day', kind: 'raw' },                   // untouched, null when absent
 *     tab:    { default: 'settings', values: [...], aliases: { 'quick-add': 'add-item' } },
 *     never:  { values: ['true'], param: 'neverLoggedIn' },      // a different URL name
 *   }
 *
 * - `default` is what an absent or unusable param reads as, and what the writer
 *   deletes instead of writing.
 * - `values` is an allowlist: anything else reads as the default, so a stale or
 *   hand-mangled link degrades to "no filter" rather than an API 400.
 * - `aliases` maps an old spelling to the current one before the allowlist.
 * - `trim: false` keeps surrounding whitespace (ids and keys are read as they
 *   are; text is trimmed). An empty value reads as the default either way.
 * - `kind`: `boolean`, `number` (any finite number), `page` (`parseInt`, and
 *   only above 0), or `raw` (the string exactly as the address has it, `null`
 *   when absent — for a param its caller resolves against something else; its
 *   `default` is the writer's alone).
 */

const specOf = (spec, key) => (spec && spec[key]) || {};

/** The URL name a key is written under. */
export const paramName = (spec, key) => specOf(spec, key).param || key;

export function readUrlParam(searchParams, key, entry = {}) {
	const raw = searchParams.get(entry.param || key);
	if (entry.kind === 'raw') return raw;
	if (raw === null) return entry.default;
	if (entry.kind === 'boolean') return raw === 'true' ? true : entry.default;
	if (entry.kind === 'number') {
		const n = Number(raw);
		return Number.isFinite(n) ? n : entry.default;
	}
	if (entry.kind === 'page') {
		const n = Number.parseInt(raw, 10);
		return Number.isFinite(n) && n > 0 ? n : entry.default;
	}
	let text = entry.trim === false ? raw : raw.trim();
	if (entry.aliases && Object.prototype.hasOwnProperty.call(entry.aliases, text)) text = entry.aliases[text];
	if (Array.isArray(entry.values) && !entry.values.includes(text)) return entry.default;
	if (text === '') return entry.default;
	return text;
}

/** Every key of the spec, read. */
export function readUrlParams(searchParams, spec = {}) {
	const out = {};
	Object.entries(spec).forEach(([key, entry]) => {
		out[key] = readUrlParam(searchParams, key, entry || {});
	});
	return out;
}

/**
 * `prev` with `patch` applied, as a new URLSearchParams. A value that is
 * undefined, null, '' or false is DELETED, and so is one equal to its default
 * unless `keepDefaults` (a choice that must be written out loud, such as the
 * default sort chosen under a layout's own). Keys outside the spec are written
 * under their own name; params the patch does not name are left alone.
 */
export function writeUrlParams(prev, patch, spec = {}, { keepDefaults = false } = {}) {
	const next = new URLSearchParams(prev);
	Object.entries(patch || {}).forEach(([key, value]) => {
		const entry = specOf(spec, key);
		const name = entry.param || key;
		const blank = value === undefined || value === null || value === '' || value === false;
		if (blank || (!keepDefaults && `${value}` === `${entry.default}`)) next.delete(name);
		else next.set(name, `${value}`);
	});
	return next;
}
